/**
 * Publish state machine + pending-edit count, lifted out of AdminShell so
 * the standalone Pages editor topbar can share it.
 *
 * State: 'idle' | 'publishing' | 'done' | 'error'. 'done' resets to 'idle'
 * after 8s. publish() is a no-op while 'publishing' OR 'done' — the done
 * button reads as inert and an accidental click must not fire a second
 * deploy.
 *
 * pendingCount refreshes on mount and every 30s; callers with edit surfaces
 * (the Pages editor) also call refreshPending() when their autosave lands,
 * since a poll-only count lags in-page edits by up to 30s.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from './config';
import { authHeaders, getSession } from './auth';
import { triggerPublish } from './publish';

export type PublishState = 'idle' | 'publishing' | 'done' | 'error';

export function usePublish() {
  const [state, setState] = useState<PublishState>('idle');
  const [msg, setMsg] = useState<string | null>(null);
  const [publishedAt, setPublishedAt] = useState<Date | null>(null);
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  // publish() guards on the CURRENT state, not a render-time closure.
  const stateRef = useRef(state);
  stateRef.current = state;
  const doneTimer = useRef<number | null>(null);

  const refreshPending = useCallback(async () => {
    try {
      if (!getSession()) return;
      const res = await fetch(`${API_BASE}/api/admin/publish/pending`, {
        headers: authHeaders(false),
      });
      if (!res.ok) return;
      const data = (await res.json()) as { count: number };
      setPendingCount(data.count);
    } catch {
      /* keep last known count on failure */
    }
  }, []);

  useEffect(() => {
    void refreshPending();
    const id = window.setInterval(() => void refreshPending(), 30_000);
    return () => {
      window.clearInterval(id);
      if (doneTimer.current) window.clearTimeout(doneTimer.current);
    };
  }, [refreshPending]);

  const publish = useCallback(async () => {
    if (stateRef.current === 'publishing' || stateRef.current === 'done') return;
    setState('publishing');
    setMsg(null);
    const r = await triggerPublish();
    if (r.ok) {
      setState('done');
      setPublishedAt(new Date());
      setMsg('Live in ~30-60s');
      setPendingCount(0);
      doneTimer.current = window.setTimeout(() => setState('idle'), 8000);
    } else {
      setState('error');
      // A 503 means the deploy hook isn't configured yet — the API returns
      // a setup hint in the body, which triggerPublish passes through as
      // the reason. Always surface it.
      setMsg(r.reason ?? 'Publish failed');
    }
  }, []);

  return { state, msg, publishedAt, pendingCount, publish, refreshPending };
}
